// Sales, powered by the connected POS (Toast/Square): this location's running
// day + trend + top sellers, and every store side by side. Refresh pulls the
// latest day on demand — as close to real-time as the POS allows.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Loader2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api, { authApi } from '../services/api';

const money = (c: number) => `$${(c / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const money2 = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const nyDate = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }).replace(/-/g, '');

export default function LiveSalesPage() {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);

  const { data: status, isLoading: statusLoading, refetch } = useQuery({
    queryKey: ['toast-status'],
    queryFn: async () => (await api.get('/v1/integrations/toast/status')).data?.data,
    retry: 2,
  });
  const { data: overview } = useQuery({
    queryKey: ['owner-overview'],
    queryFn: async () => (await api.get('/v1/owner/overview')).data?.data,
  });
  const { data: insight } = useQuery({
    queryKey: ['insight-days'],
    queryFn: async () => (await api.get('/v1/integrations/toast/insight-days')).data?.data,
  });

  const days: any[] = (status?.salesSummary?.days?.length ? status.salesSummary.days : insight?.days) || [];
  const today = days.find((d) => String(d.businessDate) === nyDate()) || days[0];
  const yesterday = [...days].sort((a, b) => String(b.businessDate).localeCompare(String(a.businessDate)))
    .find((d) => String(d.businessDate) < nyDate());
  // Last 7 COMPLETE days — today runs in its own card.
  const last7 = [...days].filter((d) => String(d.businessDate) !== nyDate())
    .sort((a, b) => String(b.businessDate).localeCompare(String(a.businessDate))).slice(0, 7);
  const weekGross = last7.reduce((s, d) => s + (d.grossCents || 0), 0);
  const weekOrders = last7.reduce((s, d) => s + (d.orders || 0), 0);

  // top sellers across the last 7 days
  const itemTotals: Record<string, { qty: number; gross: number }> = {};
  for (const d of last7) for (const [name, v] of Object.entries((d.items || {}) as Record<string, any>)) {
    const t = itemTotals[name] || { qty: 0, gross: 0 }; t.qty += v.quantity; t.gross += v.grossCents; itemTotals[name] = t;
  }
  const topItems = Object.entries(itemTotals).sort((a, b) => b[1].gross - a[1].gross).slice(0, 10);

  const trend = [...days].sort((a, b) => String(a.businessDate).localeCompare(String(b.businessDate))).slice(-21);
  const maxDay = Math.max(1, ...trend.map((d) => d.grossCents || 0));

  const refresh = async () => {
    setRefreshing(true);
    try { await api.post('/v1/integrations/toast/sync', { days: 1 }); await refetch(); } finally { setRefreshing(false); }
  };

  const openVenue = async (id: string, current: boolean) => {
    if (current) { navigate('/'); return; }
    setSwitching(id);
    try {
      const r = await authApi.switchRestaurant(id);
      localStorage.setItem('manager_token', r.data.data.accessToken);
      localStorage.setItem('manager_refresh_token', r.data.data.refreshToken);
      localStorage.setItem('manager_current_restaurant', JSON.stringify(r.data.data.restaurant));
      window.location.href = '/sales';
    } catch { setSwitching(null); }
  };
  const currentId = (() => { try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || '{}')?.id; } catch { return null; } })();

  const fmtSync = status?.lastSyncAt ? new Date(status.lastSyncAt).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;

  // Hub mode: this venue doesn't ring its own sales (a commissary) but the
  // company does — show every store instead of an empty page.
  const companySales = (overview?.venues || []).some((v: any) => (v.salesWeekCents || 0) > 0);
  const hubMode = !status?.connected && companySales;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales</h1>
          <p className="text-slate-500">{hubMode ? 'This location doesn\u2019t ring its own sales \u2014 every store, live from Toast.' : status?.locationName ? `${status.locationName} — live from your POS` : 'Live from your connected POS'}</p>
        </div>
        {status?.connected && (
          <button onClick={refresh} disabled={refreshing} className="btn btn-secondary flex items-center gap-2">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        )}
      </div>

      {statusLoading ? (
        <div className="card p-6"><p className="text-slate-400">Loading sales…</p></div>
      ) : !status?.connected && !(insight?.connected) && !hubMode ? (
        <div className="card p-6"><p className="text-slate-500">Connect your POS in Settings → Integrations and your sales appear here, per location and across all stores.</p></div>
      ) : hubMode && !(insight?.connected) ? (
        <div className="card overflow-x-auto">
          <div className="px-5 pt-5"><p className="text-xs uppercase tracking-wide text-slate-400">All locations</p></div>
          <table className="w-full mt-2">
            <thead className="bg-slate-50"><tr>
              <th className="table-header text-left">Location</th>
              <th className="table-header text-right">Today</th>
              <th className="table-header text-right">Last 7 days</th>
              <th className="table-header"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {[...(overview?.venues || [])].filter((v: any) => (v.salesWeekCents || 0) > 0 || (v.salesTodayCents || 0) > 0).sort((a: any, b: any) => (b.salesWeekCents || 0) - (a.salesWeekCents || 0)).map((v: any) => (
                <tr key={v.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openVenue(v.id, v.id === currentId)}>
                  <td className="table-cell font-medium text-slate-900">{v.name}</td>
                  <td className="table-cell text-right tabular-nums">{v.salesTodayCents ? money(v.salesTodayCents) : '—'}</td>
                  <td className="table-cell text-right tabular-nums font-semibold">{(v.salesWeekCents || 0) ? money(v.salesWeekCents) : '—'}</td>
                  <td className="table-cell text-right">{switching === v.id ? <Loader2 className="w-4 h-4 animate-spin inline text-slate-400" /> : <ArrowRight className="w-4 h-4 inline text-slate-300" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          {/* this location */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="card p-5">
              <p className="text-xs uppercase tracking-wide text-slate-400">Today{today ? '' : ' (latest day)'}</p>
              <p className="text-3xl font-semibold text-slate-900 tabular-nums mt-1">{money(today?.grossCents || 0)}</p>
              <p className="text-xs text-slate-500 mt-1">
                {(today?.grossCents || 0) === 0 && yesterday
                  ? `The day's just starting — yesterday closed at ${money(yesterday.grossCents)}.`
                  : `${today?.orders || 0} orders${fmtSync ? ` · as of ${fmtSync}` : ''}`}
              </p>
            </div>
            <div className="card p-5">
              <p className="text-xs uppercase tracking-wide text-slate-400">Last 7 days</p>
              <p className="text-3xl font-semibold text-slate-900 tabular-nums mt-1">{money(weekGross)}</p>
              <p className="text-xs text-slate-500 mt-1">{weekOrders.toLocaleString('en-US')} orders · net sales, as Toast reports</p>
            </div>
            <div className="card p-5">
              <p className="text-xs uppercase tracking-wide text-slate-400">Avg order</p>
              <p className="text-3xl font-semibold text-slate-900 tabular-nums mt-1">{weekOrders ? money2(Math.round(weekGross / weekOrders)) : '—'}</p>
              <p className="text-xs text-slate-500 mt-1">last 7 days</p>
            </div>
          </div>

          <div className="card p-5">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Daily sales · last 3 weeks</p>
            <div className="flex items-end gap-1 h-32">
              {trend.map((d) => (
                <div key={d.businessDate} className="flex-1 bg-primary-500/80 rounded-sm hover:bg-primary-600 transition-colors"
                  title={`${d.businessDate}: ${money(d.grossCents)} · ${d.orders} orders`}
                  style={{ height: `${Math.max(4, Math.round(((d.grossCents || 0) / maxDay) * 100))}%` }} />
              ))}
            </div>
          </div>

          <div className="card p-5">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Top sellers · last 7 days</p>
            <div className="space-y-1.5">
              {topItems.map(([name, v]) => (
                <div key={name} className="flex items-baseline justify-between text-sm">
                  <span className="text-slate-700">{name}</span>
                  <span className="tabular-nums text-slate-500">{v.qty.toLocaleString('en-US')} sold · <span className="font-medium text-slate-900">{money(v.gross)}</span></span>
                </div>
              ))}
            </div>
          </div>

          {/* all locations */}
          {(overview?.venues?.length || 0) > 1 && (
            <div className="card overflow-x-auto">
              <div className="px-5 pt-5"><p className="text-xs uppercase tracking-wide text-slate-400">All locations</p></div>
              <table className="w-full mt-2">
                <thead className="bg-slate-50"><tr>
                  <th className="table-header text-left">Location</th>
                  <th className="table-header text-right">Today</th>
                  <th className="table-header text-right">Last 7 days</th>
                  <th className="table-header"></th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {[...overview.venues].sort((a: any, b: any) => (b.salesWeekCents || 0) - (a.salesWeekCents || 0)).map((v: any) => (
                    <tr key={v.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openVenue(v.id, v.id === currentId)}>
                      <td className="table-cell font-medium text-slate-900">{v.name}{v.id === currentId && <span className="badge badge-info ml-2">current</span>}</td>
                      <td className="table-cell text-right tabular-nums">{v.salesTodayCents ? money(v.salesTodayCents) : '—'}</td>
                      <td className="table-cell text-right tabular-nums font-semibold">{(v.salesWeekCents || 0) ? money(v.salesWeekCents) : '—'}</td>
                      <td className="table-cell text-right">{switching === v.id ? <Loader2 className="w-4 h-4 animate-spin inline text-slate-400" /> : <ArrowRight className="w-4 h-4 inline text-slate-300" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
